import React, { useCallback, useEffect, useRef } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { RetailerPromotionsSerializer } from "web/types/serializers";
import * as styles from "./promotion-summary.css";

export interface PromotionSummaryProps {
    promotions: RetailerPromotionsSerializer;
    title: string;
    onClick: () => void;
}

export function PromotionSummary({
    promotions,
    title,
    onClick,
}: PromotionSummaryProps): React.ReactElement {
    const { active_promotions } = promotions;
    const promotionsCouponRef = useRef<HTMLDivElement | null>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 1,
            once: true,
        },
        promotionsCouponRef
    );

    useEffect(() => {
        if (isVisible) {
            analytics.event(
                "promotion_module_see_all",
                "impression",
                "",
                true,
                {},
                "",
                {},
                [{ retailer_network: title }],
                false,
                false
            );
        }
    }, [isVisible, title]);

    const activePromotionsTitle =
        active_promotions?.length === 1
            ? gettext("promotion_message.promotion_text.single_active_promotion")
            : gettext("promotion_message.promotion_text.active_promotions", {
                  number: active_promotions.length.toString(),
              });

    const handleClick = useCallback(() => {
        onClick();
        analytics.event(
            "promotion_module_see_all",
            "clicked",
            "",
            false,
            {},
            "",
            {},
            [{ retailer_network: title }],
            false,
            false
        );
    }, [onClick, title]);

    return (
        <>
            <div
                className={styles.promotionSummaryContainer}
                data-testid="promo-summary"
                ref={promotionsCouponRef}
                onClick={handleClick}
            >
                <div className={styles.promoSummaryTextWrapper}>
                    <SVGIcon name="tag-fill" className={styles.tagIcon} flip />
                    <Text as="p" textStyle="body-s">
                        {activePromotionsTitle}
                    </Text>
                </div>
                <Button
                    title={gettext("promotion.button.see_all")}
                    size="sm"
                    variant="primary"
                    onClick={handleClick}
                />
            </div>
        </>
    );
}
